// === INPACT:SLUITSTRAAT:BOILERPLATE:TEXTS-PAGE:v1 ===

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useConfig,
  SettingsHeader,
  SettingsFooter,
} from '@sluitstraat/core';

interface GeneralTexts {
  browserTitle: string;
  applicationName: string;
  tagline: string;
}

interface ContactInfo {
  email: string;
  phone: string;
  website: string;
}

// Info icon with tooltip component
const InfoTooltip: React.FC<{ text: string }> = ({ text }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <span
      style={tooltipStyles.container}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      <span style={tooltipStyles.icon}>i</span>
      {isVisible && (
        <span style={tooltipStyles.tooltip}>{text}</span>
      )}
    </span>
  );
};

const tooltipStyles = {
  container: {
    position: 'relative' as const,
    display: 'inline-flex',
    marginLeft: '8px',
    cursor: 'help',
  },
  icon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    backgroundColor: '#9ca3af',
    color: 'white',
    fontSize: '11px',
    fontWeight: '600' as const,
    fontStyle: 'italic' as const,
  },
  tooltip: {
    position: 'absolute' as const,
    bottom: '24px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: '#1f2937',
    color: 'white',
    padding: '8px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    whiteSpace: 'nowrap' as const,
    zIndex: 1000,
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  },
};

const TextsPage: React.FC = () => {
  const { config, reloadConfig } = useConfig();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Initialize general texts from config
  const [formData, setFormData] = useState<GeneralTexts>({
    browserTitle: config.texts?.general?.browserTitle || config.branding?.appName || '',
    applicationName: config.texts?.general?.applicationName || config.branding?.appName || '',
    tagline: config.texts?.general?.tagline || config.customer?.tagline || '',
  });

  // Initialize contact info from config
  const [contactData, setContactData] = useState<ContactInfo>({
    email: config.texts?.contact?.email || config.customer?.contact?.email || '',
    phone: config.texts?.contact?.phone || config.customer?.contact?.phone?.main || '',
    website: config.texts?.contact?.website || config.customer?.contact?.website || '',
  });

  const handleInputChange = (field: keyof GeneralTexts, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleContactChange = (field: keyof ContactInfo, value: string) => {
    setContactData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setNotification(null);

    try {
      // Save texts to config.json via API
      const response = await fetch('/api/texts-content', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          general: formData,
          contact: contactData,
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to save content');
      }

      setNotification({ type: 'success', message: t('settings.content.savedSuccessfully') });

      // Reload the config to get fresh data
      try {
        await reloadConfig();
      } catch (configError) {
        // Continue even if reload fails
      }

      // Navigate back to settings page
      setTimeout(() => {
        navigate('/settings', { replace: true });
      }, 1500);
    } catch (error) {
      console.error('Error saving content:', error);
      setNotification({ type: 'error', message: t('settings.content.saveError') });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/settings');
  };

  return (
    <>
      <SettingsHeader />
      <div style={styles.container}>
        <div style={styles.content}>
          <header style={styles.header}>
            <button onClick={handleCancel} style={styles.backButton}>
              &#8592; {t('settings.content.backToSettings')}
            </button>
            <h1 style={styles.title}>{t('settings.content.title')}</h1>
            <p style={styles.subtitle}>{t('settings.content.subtitle')}</p>
          </header>

          {notification && (
            <div style={{
              ...styles.notification,
              ...(notification.type === 'success' ? styles.notificationSuccess : styles.notificationError)
            }}>
              <span style={styles.notificationIcon}>
                {notification.type === 'success' ? '✓' : '!'}
              </span>
              {notification.message}
            </div>
          )}

          <form onSubmit={handleSubmit} style={styles.form}>
            {/* General Application Texts */}
            <div style={styles.section}>
              <div style={styles.sectionHeader}>
                <h2 style={styles.sectionTitle}>{t('settings.content.generalTexts.title')}</h2>
                <p style={styles.sectionDescription}>
                  {t('settings.content.generalTexts.description')}
                </p>
              </div>

              <div style={styles.sectionContent}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    {t('settings.content.generalTexts.browserTitle')}
                    <InfoTooltip text={t('settings.content.generalTexts.browserTitleTooltip')} />
                  </label>
                  <input
                    type="text"
                    style={styles.input}
                    value={formData.browserTitle}
                    onChange={(e) => handleInputChange('browserTitle', e.target.value)}
                    placeholder={t('settings.content.generalTexts.browserTitlePlaceholder')}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    {t('settings.content.generalTexts.applicationName')}
                    <InfoTooltip text={t('settings.content.generalTexts.applicationNameTooltip')} />
                  </label>
                  <input
                    type="text"
                    style={styles.input}
                    value={formData.applicationName}
                    onChange={(e) => handleInputChange('applicationName', e.target.value)}
                    placeholder={t('settings.content.generalTexts.applicationNamePlaceholder')}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    {t('settings.content.generalTexts.tagline')}
                    <InfoTooltip text={t('settings.content.generalTexts.taglineTooltip')} />
                  </label>
                  <input
                    type="text"
                    style={styles.input}
                    value={formData.tagline}
                    onChange={(e) => handleInputChange('tagline', e.target.value)}
                    placeholder={t('settings.content.generalTexts.taglinePlaceholder')}
                  />
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div style={styles.section}>
              <div style={styles.sectionHeader}>
                <h2 style={styles.sectionTitle}>{t('settings.content.contact.title')}</h2>
                <p style={styles.sectionDescription}>
                  {t('settings.content.contact.description')}
                </p>
              </div>

              <div style={styles.sectionContent}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    {t('settings.content.contact.email')}
                    <InfoTooltip text={t('settings.content.contact.emailTooltip')} />
                  </label>
                  <input
                    type="email"
                    style={styles.input}
                    value={contactData.email}
                    onChange={(e) => handleContactChange('email', e.target.value)}
                    placeholder="info@company.com"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    {t('settings.content.contact.phone')}
                    <InfoTooltip text={t('settings.content.contact.phoneTooltip')} />
                  </label>
                  <input
                    type="tel"
                    style={styles.input}
                    value={contactData.phone}
                    onChange={(e) => handleContactChange('phone', e.target.value)}
                    placeholder="+31 123 456 789"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    {t('settings.content.contact.website')}
                    <InfoTooltip text={t('settings.content.contact.websiteTooltip')} />
                  </label>
                  <input
                    type="url"
                    style={styles.input}
                    value={contactData.website}
                    onChange={(e) => handleContactChange('website', e.target.value)}
                    placeholder="https://www.company.com"
                  />
                </div>
              </div>
            </div>

            <div style={styles.buttonGroup}>
              <button
                type="button"
                onClick={handleCancel}
                style={styles.cancelButton}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                style={styles.submitButton}
                disabled={isSubmitting}
              >
                {isSubmitting ? t('settings.content.saving') : t('settings.content.saveChanges')}
              </button>
            </div>
          </form>
        </div>
      </div>
      <SettingsFooter />
    </>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#F5F5F5',
    padding: '32px 20px',
  },
  content: {
    maxWidth: '900px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '32px',
  },
  backButton: {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    color: '#6b7280',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600' as const,
    cursor: 'pointer',
    marginBottom: '16px',
    transition: 'color 0.2s',
  },
  title: {
    margin: '0 0 8px 0',
    fontSize: '32px',
    fontWeight: '700' as const,
    color: '#1f2937',
  },
  subtitle: {
    margin: 0,
    fontSize: '16px',
    color: '#6b7280',
  },
  notification: {
    padding: '16px 20px',
    borderRadius: '8px',
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '14px',
    fontWeight: '600' as const,
  },
  notificationSuccess: {
    backgroundColor: '#d1fae5',
    color: '#065f46',
    border: '2px solid #10b981',
  },
  notificationError: {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    border: '2px solid #ef4444',
  },
  notificationIcon: {
    fontSize: '18px',
    fontWeight: '700' as const,
  },
  form: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    padding: '24px',
  },
  section: {
    marginBottom: '24px',
  },
  sectionHeader: {
    marginBottom: '20px',
    paddingBottom: '16px',
    borderBottom: '2px solid #e5e7eb',
  },
  sectionTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600' as const,
    color: '#1f2937',
  },
  sectionDescription: {
    margin: '8px 0 0 0',
    fontSize: '14px',
    color: '#6b7280',
  },
  sectionContent: {
    padding: '0',
  },
  formGroup: {
    marginBottom: '16px',
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '6px',
    fontSize: '14px',
    fontWeight: '600' as const,
    color: '#374151',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'inherit',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box' as const,
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '24px',
    paddingTop: '24px',
    borderTop: '1px solid #e5e7eb',
  },
  cancelButton: {
    padding: '12px 24px',
    backgroundColor: 'transparent',
    color: '#6b7280',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600' as const,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  submitButton: {
    padding: '12px 24px',
    backgroundColor: 'var(--primary-color, #056666)',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600' as const,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
};

export default TextsPage;
